import React, { useState } from "react";
import { FaFileUpload } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { toast } from 'react-toastify';
import { uploadCollaborationImage } from "../../../redux/actions/action";
import { useNavigate } from "react-router-dom";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const AddCollaboratorForm = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    projectNo: "",
    organizationName: "",
    uploadPhoto: null,
    startDate: null,
    endDate: null,
  });

  const [errors, setErrors] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [imageName, setImageName] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    validateField(name, value);
  };

  const handleImageChange = (e) => {
    const imageFile = e.target.files[0];
    const allowedTypes = ["image/jpeg", "image/jpg"];

    if (imageFile && allowedTypes.includes(imageFile.type)) {
      setUploadedFile(imageFile);
      setImageName(imageFile.name);
      setFormData((prev) => ({
        ...prev,
        uploadPhoto: imageFile,
      }));
    } else {
      toast.error("Please upload a valid image file (.jpg or .jpeg only).");
    }
  };
  const validateField = (name, value, allValues = formData) => {
    let error = "";

    switch (name) {
      case "uploadPhoto":
        if (!value) error = "Logo upload is required.";
        break;

      case "organizationName":
        if (!value.trim()) error = "Organization Name is required.";
        break;

      case "projectNo":
        if (!value.trim()) error = "Project No is required.";
        break;

      case "startDate": {
        if (!value) {
          error = "Start date is required.";
        } else {
          const selectedStartDate = new Date(value);
          const minStartDate = new Date("2017-01-01");

          if (selectedStartDate < minStartDate) {
            error = "Start date must be from 2017 onwards.";
          }
        }
        break;
      }

      case "endDate": {
        if (!value) {
          error = "End date is required.";
        } else {
          const selectedEndDate = new Date(value);
          const startDate = new Date(allValues.startDate);

          if (allValues.startDate && selectedEndDate <= startDate) {
            error = "End date must be after the start date.";
          }
        }
        break;
      }

      default:
        break;
    }

    return error;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // build errors object synchronously
    const validationErrors = {};
    Object.keys(formData).forEach((field) => {
      const errorMsg = validateField(field, formData[field], formData);
      if (errorMsg) validationErrors[field] = errorMsg;
    });

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fix validation errors.");
      return;
    }

    try {
      if (uploadedFile) {
        const imageFormData = new FormData();
        imageFormData.append("image", uploadedFile);
        imageFormData.append("organizationName", formData.organizationName);
        imageFormData.append("projectNo", formData.projectNo);
        imageFormData.append("startdate", formData.startDate?.toISOString());
        imageFormData.append("enddate", formData.endDate?.toISOString());

        await dispatch(uploadCollaborationImage(imageFormData));
      }

      onSave();
      onClose();
      navigate("/");
      toast.success("Successfully added collaborator");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Submission failed.");
    }
  };


  return (
    <div className="self-stretch flex flex-col items-start justify-start text-start text-xl text-black font-poppins rounded-3xl w-[750px]">
      <form onSubmit={(e) => e.preventDefault()} className="w-full bg-color p-6 rounded-xl shadow-lg">

        {/* Logo Upload Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
  <div className="h-[150px] w-[150px] flex flex-col items-start justify-start">
    
    <label className="relative h-full w-full mx-2 flex flex-col items-center justify-center rounded-md bg-white border border-gray-300 cursor-pointer">
      {/* Icon */}
      <FaFileUpload className="w-[30px] h-[30px] mb-2 text-gray-400" />

      {/* Text */}
      <span className="text-gray-700 font-poppins text-[14px]">Upload Logo</span>

      {/* Hidden file input */}
      <input
        type="file"
        onChange={handleImageChange}
        name="uploadPhoto"
        required
        className="hidden"
      />
    </label>

    {/* File name */}
    {imageName && <p className="text-gray-600 text-xs mt-1">{imageName}</p>}

    {/* Error message */}
    {errors.uploadPhoto && <p className="text-red-500 text-sm">{errors.uploadPhoto}</p>}
  </div>
</div>


        {/* Organization Name + Project No Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
          <div className="w-full">
            <label className="block mb-2 font-poppins font-bold text-white">Organization Name*</label>
            <input
              type="text"
              name="organizationName"
              value={formData.organizationName}
              onChange={handleChange}
              className="w-full h-[45px] border border-gray-300 rounded-md px-3"
              placeholder="Enter Organization Name"
              required
            />
            {errors.organizationName && <p className="text-red-500 text-sm">{errors.organizationName}</p>}
          </div>

          <div className="w-full">
            <label className="block mb-2 font-poppins font-bold text-white">Project No*</label>
            <input
              type="text"
              name="projectNo"
              value={formData.projectNo}
              onChange={handleChange}
              className="w-full h-[45px] border border-gray-300 rounded-md px-3"
              placeholder="Enter Project No"
              required
            />
            {errors.projectNo && <p className="text-red-500 text-sm">{errors.projectNo}</p>}
          </div>
        </div>

        {/* Dates Row */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <DatePicker
              label="Start Date*"
              value={formData.startDate}
              onChange={(newValue) => handleChange({ target: { name: 'startDate', value: newValue } })}
              minDate={new Date('2017-01-01')}
              renderInput={(params) => (
                <TextField {...params} required className="w-full h-[45px]" />
              )}
            />
            {errors.startDate && <p className="text-red-500 text-sm">{errors.startDate}</p>}

            <DatePicker
              label="End Date*"
              value={formData.endDate}
              onChange={(newValue) => handleChange({ target: { name: 'endDate', value: newValue } })}
              minDate={new Date('2017-01-01')}
              renderInput={(params) => (
                <TextField {...params} required className="w-full h-[45px]" />
              )}
            />
            {errors.endDate && <p className="text-red-500 text-sm">{errors.endDate}</p>}
          </div>
        </LocalizationProvider>

        {/* Buttons */}
        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={onClose}
            className="bg-black text-white py-2 px-6 rounded-lg mr-4"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSave}
            className="bg-black text-white py-2 px-6 rounded-lg"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCollaboratorForm;
